/**
 * スロット作成ダイアログ
 *
 * カレンダー / 日ビューの空きスロットをクリックした際に表示する
 * タスク作成・予定作成・既存タスク配置の 3 モードを持つ共通コンポーネント
 */

import { useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'

import type { TodoResponse } from '@/api/types/todo'
import { Button } from '@/components/ui/button'
import { SearchBox } from '@/components/ui/search-box'
import { Textarea } from '@/components/ui/textarea'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { parseUtcDate } from '@/lib/date-utils'

import { ProjectPicker } from './ProjectPicker'
import { filterTodosByQuery } from './search'
import { compareTodoPosition } from './sort'

type DialogMode = 'new' | 'event' | 'existing'

const EMPTY_CHILDREN = new Map<number, TodoResponse[]>()

type Props = {
  /** ダイアログ表示用の時間範囲。省略すると日付と時刻欄を出さない */
  range?: { start: string; end: string }
  /** 既存タスクタブの確定ボタンの文言 */
  existingActionLabel?: string
  /** 閉じるコールバック */
  onClose: () => void
  /** 無効化するタブ */
  disabledModes?: DialogMode[]
  /** 出さないタブ */
  hiddenModes?: DialogMode[]
  /** 初期表示タブ */
  defaultMode?: DialogMode
  /** 時間変更コールバック */
  onTimeChange?: (start: string, end: string) => void
  /** タスク作成 */
  onCreateTask: (title: string, project: string | null) => Promise<void>
  /** 予定作成 */
  onCreateEvent: (title: string) => Promise<void>
  /** 既存タスク配置 */
  onScheduleExisting: (todoId: number) => Promise<void>
  /** 全タスク一覧（完了・却下除く） */
  todos: TodoResponse[]
}

export const SlotCreateDialog = ({
  range,
  existingActionLabel = '配置',
  onClose,
  disabledModes = [],
  hiddenModes = [],
  defaultMode = 'new',
  onTimeChange,
  onCreateTask,
  onCreateEvent,
  onScheduleExisting,
  todos,
}: Props) => {
  const initial = disabledModes.includes(defaultMode)
    ? ((['new', 'event', 'existing'] as const).find((m) => !disabledModes.includes(m)) ?? 'new')
    : defaultMode
  const [mode, setMode] = useState<DialogMode>(initial)
  const [title, setTitle] = useState('')
  const [project, setProject] = useState<string | null>(null)
  const [selectedTodoId, setSelectedTodoId] = useState<number | null>(null)
  const [query, setQuery] = useState('')
  const { isSubmitting, run } = useSubmitLock()
  const taskInputRef = useRef<HTMLTextAreaElement>(null)

  // 既存タスクをスケジュール済み / 未スケジュールに分類
  const activeTodos = useMemo(
    () =>
      filterTodosByQuery(
        todos.filter((t) => t.status !== 'done' && t.status !== 'dismissed' && !t.is_event),
        query,
        EMPTY_CHILDREN,
      ).sort(compareTodoPosition),
    [todos, query],
  )
  const unscheduledTodos = useMemo(() => activeTodos.filter((t) => !t.scheduled_start), [activeTodos])
  const scheduledTodos = useMemo(() => activeTodos.filter((t) => !!t.scheduled_start), [activeTodos])

  // 時間表示用
  const startDate = range ? new Date(range.start) : null
  const endDate = range ? new Date(range.end) : null
  const toTimeStr = (d: Date | null) =>
    d ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : ''
  const [startTime, setStartTime] = useState(toTimeStr(startDate))
  const [endTime, setEndTime] = useState(toTimeStr(endDate))

  const handleTimeChange = (start: string, end: string) => {
    setStartTime(start)
    setEndTime(end)
    onTimeChange?.(start, end)
  }

  const handleSubmit = async () => {
    await run(async () => {
      try {
        if (mode === 'new') {
          if (!title.trim()) return
          await onCreateTask(title.trim(), project)
        } else if (mode === 'event') {
          if (!title.trim()) return
          await onCreateEvent(title.trim())
        } else {
          if (!selectedTodoId) return
          await onScheduleExisting(selectedTodoId)
        }
        onClose()
      } catch (err) {
        toast.error(err instanceof Error ? err.message : '操作に失敗しました')
      }
    })
  }

  const allTabItems: { key: DialogMode; label: string; activeColor: string }[] = [
    { key: 'new', label: 'タスク', activeColor: 'border-primary text-primary' },
    { key: 'existing', label: '既存タスク', activeColor: 'border-primary text-primary' },
    { key: 'event', label: '予定', activeColor: 'border-primary text-primary' },
  ]
  const tabItems = allTabItems.filter((tab) => !hiddenModes.includes(tab.key))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-background rounded-lg p-4 w-95 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {startDate && (
          <>
            {/* 日付ヘッダー */}
            <p className="text-sm text-muted-foreground mb-2">
              {startDate.toLocaleDateString('ja', { month: 'numeric', day: 'numeric', weekday: 'short' })}
            </p>

            {/* 時間編集エリア */}
            <div className="flex items-center gap-2 mb-3">
              <input
                type="time"
                className="bg-muted rounded px-2 py-1 text-sm border border-border"
                value={startTime}
                onChange={(e) => handleTimeChange(e.target.value, endTime)}
              />
              <span className="text-muted-foreground">〜</span>
              <input
                type="time"
                className="bg-muted rounded px-2 py-1 text-sm border border-border"
                value={endTime}
                onChange={(e) => handleTimeChange(startTime, e.target.value)}
              />
            </div>
          </>
        )}

        {/* モード切替タブ */}
        <div className="flex border-b border-border mb-3">
          {tabItems.map((tab) => {
            const isDisabled = disabledModes.includes(tab.key)
            const isActive = mode === tab.key
            return (
              <button
                key={tab.key}
                type="button"
                disabled={isDisabled}
                className={`flex-1 p-0! text-sm font-medium border-b-2 transition-colors ${
                  isDisabled
                    ? 'border-transparent text-muted-foreground/40 cursor-not-allowed'
                    : isActive
                      ? tab.activeColor
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
                onClick={() => {
                  if (isDisabled) return
                  setMode(tab.key)
                  setTitle('')
                  if (tab.key === 'event') setTimeout(() => taskInputRef.current?.focus(), 50)
                  if (tab.key === 'new') setTimeout(() => taskInputRef.current?.focus(), 50)
                }}
              >
                {tab.label}
              </button>
            )
          })}
        </div>

        {/* コンテンツエリア（高さ統一） */}
        <div className="min-h-70 flex flex-col">
          {/* タスク / 予定 作成フォーム */}
          {(mode === 'new' || mode === 'event') && (
            <form
              className="flex flex-col flex-1"
              onSubmit={(e) => {
                e.preventDefault()
                void handleSubmit()
              }}
            >
              <Textarea
                ref={taskInputRef}
                className="flex-1 resize-none"
                placeholder={mode === 'event' ? '予定名を入力（筋トレ、散歩 等）...' : 'タスク名を入力...'}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && !e.nativeEvent.isComposing) {
                    e.preventDefault()
                    void handleSubmit()
                  }
                }}
              />
              {mode === 'new' && (
                <div className="pt-2">
                  <ProjectPicker todos={todos} value={project} onChange={setProject} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mt-auto pt-3">
                <Button size="sm" variant="ghost" type="button" onClick={onClose}>
                  キャンセル
                </Button>
                <Button size="sm" type="submit" disabled={isSubmitting || !title.trim()}>
                  {isSubmitting ? '作成中...' : '作成'}
                </Button>
              </div>
            </form>
          )}

          {/* 既存タスク配置 */}
          {mode === 'existing' && (
            <div className="flex flex-col flex-1">
              <div className="mb-2">
                <SearchBox
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value)
                    setSelectedTodoId(null)
                  }}
                  placeholder="タイトル・プロジェクト・タグ・#ID"
                />
              </div>
              {activeTodos.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">
                  {query.trim() ? '検索に一致するタスクがありません' : '配置可能なタスクがありません'}
                </p>
              ) : (
                <div className="max-h-56 overflow-y-auto space-y-3">
                  {/* 未スケジュール */}
                  {unscheduledTodos.length > 0 && (
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1 px-1">
                        未スケジュール
                      </p>
                      <div className="space-y-0.5">
                        {unscheduledTodos.map((t) => (
                          <TaskListItem
                            key={t.id}
                            todo={t}
                            selected={selectedTodoId === t.id}
                            onSelect={() => setSelectedTodoId(t.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {/* スケジュール済み */}
                  {scheduledTodos.length > 0 && (
                    <div>
                      <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wide mb-1 px-1">
                        スケジュール済み
                      </p>
                      <div className="space-y-0.5">
                        {scheduledTodos.map((t) => (
                          <TaskListItem
                            key={t.id}
                            todo={t}
                            selected={selectedTodoId === t.id}
                            onSelect={() => setSelectedTodoId(t.id)}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-2 mt-auto pt-3">
                <Button size="sm" variant="ghost" type="button" onClick={onClose}>
                  キャンセル
                </Button>
                <Button
                  size="sm"
                  type="button"
                  disabled={isSubmitting || !selectedTodoId}
                  onClick={() => void handleSubmit()}
                >
                  {isSubmitting ? `${existingActionLabel}中...` : existingActionLabel}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/** 既存タスクリストの1行 */
const TaskListItem = ({
  todo,
  selected,
  onSelect,
}: {
  todo: TodoResponse
  selected: boolean
  onSelect: () => void
}) => {
  const scheduleLabel = todo.scheduled_start
    ? parseUtcDate(todo.scheduled_start).toLocaleString('ja', {
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  return (
    <button
      type="button"
      className={`w-full text-left px-2.5 py-1! rounded-md text-sm transition-colors flex items-center gap-2 ${
        selected ? 'bg-primary/10 ring-1 ring-primary/40 text-primary font-medium' : 'hover:bg-muted/60 text-foreground'
      }`}
      onClick={onSelect}
    >
      <div
        className={`size-3.5 rounded-full shrink-0 border-2 flex items-center justify-center ${
          selected ? 'border-primary bg-primary' : 'border-muted-foreground/40'
        }`}
      >
        {selected && <div className="size-1.5 rounded-full bg-white" />}
      </div>
      <div className="flex-1 min-w-0">
        <span className="line-clamp-1">{todo.title}</span>
        <div className="flex items-center gap-1.5 mt-0.5">
          {scheduleLabel && (
            <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">📅 {scheduleLabel}</span>
          )}
          {todo.estimated_minutes && (
            <span className="text-[10px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
              ⏱ {todo.estimated_minutes}分
            </span>
          )}
          {todo.priority >= 70 && (
            <span className="text-[10px] text-red-500 bg-red-500/10 px-1.5 py-0.5 rounded">優先</span>
          )}
        </div>
      </div>
    </button>
  )
}
